import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { EventVenueAvailability, EventVenueBookingCreated, PublicEventVenue } from '../types';
import CustomerEventVenueMenu from './CustomerEventVenueMenu';
import { setBookingLayoutAccess } from '../utils/bookingLayoutAccess';

interface BookingForm {
  eventVenueId: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  customerName: string;
  phone: string;
  email: string;
  guestCount: string;
  customerNotes: string;
}

const initialForm: BookingForm = {
  eventVenueId: '',
  eventDate: '',
  startTime: '',
  endTime: '',
  customerName: '',
  phone: '',
  email: '',
  guestCount: '',
  customerNotes: '',
};

const getAvailabilityKey = (form: BookingForm) =>
  JSON.stringify([form.eventVenueId, form.eventDate, form.startTime, form.endTime]);

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.response?.data?.title || fallback;

const getUkraineDate = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

const addDays = (date: string, days: number) => {
  if (!date) return '';
  const [year, month, day] = date.split('-').map(Number);
  const result = new Date(Date.UTC(year, month - 1, day + days));
  return result.toISOString().slice(0, 10);
};

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('uk-UA', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Kyiv',
  }).format(new Date(value));

const EventVenueBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<PublicEventVenue[]>([]);
  const [form, setForm] = useState<BookingForm>(initialForm);
  const formRef = useRef(form);
  const [availability, setAvailability] = useState<EventVenueAvailability | null>(null);
  const [createdBooking, setCreatedBooking] = useState<EventVenueBookingCreated | null>(null);
  const [activeMenuAccess, setActiveMenuAccess] = useState<{
    bookingId: number;
    customerAccessToken: string;
  } | null>(null);
  const [bookingIdInput, setBookingIdInput] = useState('');
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [accessError, setAccessError] = useState('');
  const [accessCodeVisible, setAccessCodeVisible] = useState(false);
  const [copyNotice, setCopyNotice] = useState('');
  const [copyError, setCopyError] = useState('');
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [availabilityKey, setAvailabilityKey] = useState('');
  const availabilityRequestId = useRef(0);
  const currentAvailabilityKey = getAvailabilityKey(form);
  formRef.current = form;

  useEffect(() => {
    let active = true;
    api.get<PublicEventVenue[]>('/event-venue-bookings/venues')
      .then(({ data }) => {
        if (active) setVenues(data);
      })
      .catch((requestError) => {
        if (active) setError(getErrorMessage(requestError, 'Не вдалося завантажити доступні зали.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const selectedVenue = useMemo(
    () => venues.find((venue) => venue.id === Number(form.eventVenueId)),
    [form.eventVenueId, venues]
  );
  const minimumDate = selectedVenue
    ? addDays(getUkraineDate(), selectedVenue.minimumBookingNoticeDays)
    : getUkraineDate();
  const maximumDate = selectedVenue
    ? addDays(getUkraineDate(), selectedVenue.maximumBookingAdvanceDays)
    : undefined;

  const invalidateAvailability = () => {
    availabilityRequestId.current += 1;
    setAvailability(null);
    setAvailabilityKey('');
    setChecking(false);
  };

  const updateForm = (field: keyof BookingForm, value: string) => {
    if (field === 'eventVenueId' || field === 'eventDate' || field === 'startTime' || field === 'endTime') {
      invalidateAvailability();
    }
    setForm((current) => ({ ...current, [field]: value }));
    setCreatedBooking(null);
    setError('');
  };

  const validateTime = () => {
    if (!form.startTime || !form.endTime) return 'Оберіть час початку та завершення.';
    const [startHour, startMinute] = form.startTime.split(':').map(Number);
    const [endHour, endMinute] = form.endTime.split(':').map(Number);
    const duration = endHour * 60 + endMinute - startHour * 60 - startMinute;
    if (duration < 30 || duration > 18 * 60) {
      return 'Тривалість події має бути від 30 хвилин до 18 годин.';
    }
    return '';
  };

  const checkAvailability = async () => {
    setError('');
    if (!form.eventVenueId || !form.eventDate) {
      setError('Оберіть зал і дату події.');
      return;
    }
    const timeError = validateTime();
    if (timeError) {
      setError(timeError);
      return;
    }

    const requestId = ++availabilityRequestId.current;
    const requestKey = getAvailabilityKey(form);
    setChecking(true);
    setAvailability(null);
    setAvailabilityKey('');
    try {
      const { data } = await api.get<EventVenueAvailability>('/event-venue-bookings/availability', {
        params: {
          venueId: Number(form.eventVenueId),
          date: form.eventDate,
          startTime: `${form.startTime}:00`,
          endTime: `${form.endTime}:00`,
        },
      });
      if (requestId !== availabilityRequestId.current || requestKey !== getAvailabilityKey(formRef.current)) return;
      setAvailability(data);
      setAvailabilityKey(requestKey);
    } catch (requestError) {
      if (requestId === availabilityRequestId.current && requestKey === getAvailabilityKey(formRef.current)) {
        setError(getErrorMessage(requestError, 'Не вдалося перевірити доступність залу.'));
      }
    } finally {
      if (requestId === availabilityRequestId.current) setChecking(false);
    }
  };

  const submitBooking = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!availability?.isAvailable || availabilityKey !== currentAvailabilityKey) {
      setError('Спочатку перевірте доступність залу на обраний час.');
      return;
    }
    const timeError = validateTime();
    if (timeError) {
      setError(timeError);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post<EventVenueBookingCreated>('/event-venue-bookings', {
        eventVenueId: Number(form.eventVenueId),
        eventDate: form.eventDate,
        startTime: `${form.startTime}:00`,
        endTime: `${form.endTime}:00`,
        customerName: form.customerName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        guestCount: Number(form.guestCount),
        customerNotes: form.customerNotes.trim(),
      });
      setCreatedBooking(data);
      setActiveMenuAccess({
        bookingId: data.bookingId,
        customerAccessToken: data.customerAccessToken,
      });
      setAvailability(null);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Не вдалося створити заявку на бронювання.'));
      setAvailability(null);
    } finally {
      setSubmitting(false);
    }
  };

  const openExistingMenu = (event: FormEvent) => {
    event.preventDefault();
    const bookingId = Number(bookingIdInput);
    const token = accessCodeInput.trim();
    if (!Number.isSafeInteger(bookingId) || bookingId <= 0 || !/^[A-F0-9]{64}$/.test(token)) {
      setAccessError('Введіть номер бронювання та приватний код із 64 великих латинських літер або цифр.');
      return;
    }
    setAccessError('');
    setActiveMenuAccess({ bookingId, customerAccessToken: token });
  };

  const closePrivateMenu = () => {
    setActiveMenuAccess(null);
    setAccessCodeInput('');
    setAccessCodeVisible(false);
    setCopyNotice('');
    setCopyError('');
    setError('');
  };

  const copyAccessCode = async (token: string) => {
    setCopyNotice('');
    setCopyError('');
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Буфер обміну недоступний у цьому браузері.');
      }
      await navigator.clipboard.writeText(token);
      setCopyNotice('Приватний код скопійовано. Зберігайте його у безпечному місці.');
    } catch (copyFailure) {
      setCopyError(copyFailure instanceof Error
        ? `Не вдалося скопіювати приватний код: ${copyFailure.message}`
        : 'Не вдалося скопіювати приватний код. Скопіюйте його вручну після показу.');
    }
  };

  return (
    <main className="page-shell" style={{ maxWidth: 900 }}>
      <h2>Бронювання залу</h2>
      {loading ? <p>Завантаження доступних залів...</p> : null}
      {!loading && venues.length === 0 && !error ? (
        <p role="status">Онлайн-бронювання залів наразі недоступне.</p>
      ) : null}

      {createdBooking && (
        <section style={{
            padding: 16,
            margin: '16px 0',
            border: '1px solid var(--crm-success)',
            borderRadius: 8,
            background: 'var(--crm-success-bg)',
            color: 'var(--crm-text)',
          }}>
            <div role="status">
              <h3 style={{ marginTop: 0 }}>Заявку №{createdBooking.bookingId} передано адміністратору</h3>
              <p>
                Бронювання очікує підтвердження після внесення завдатку. Мінімальна сума:{' '}
                <strong>{createdBooking.requiredDepositAmount.toFixed(2)} ₴</strong>.
              </p>
              <p style={{ marginBottom: 0 }}>Строк оплати: {formatDateTime(createdBooking.depositDueAtUtc)}.</p>
            </div>
            {/^[A-F0-9]{64}$/.test(createdBooking.customerAccessToken) && (
              <>
                <section aria-label="Приватний код доступу">
                  <p>
                    Цей приватний код потрібен, щоб повернутися до меню бронювання. Збережіть його у безпечному місці:
                    після закриття сторінки або оновлення відновити код неможливо.
                  </p>
                  <div>
                    <button type="button" onClick={() => {
                      setAccessCodeVisible((visible) => !visible);
                      setCopyNotice('');
                      setCopyError('');
                    }}>
                      {accessCodeVisible ? 'Приховати приватний код' : 'Показати приватний код'}
                    </button>
                    {accessCodeVisible && (
                      <p>
                        <code>{createdBooking.customerAccessToken}</code>
                      </p>
                    )}
                    <button type="button" onClick={() => void copyAccessCode(createdBooking.customerAccessToken)}>
                      Скопіювати приватний код
                    </button>
                  </div>
                  {copyNotice && <p role="status">{copyNotice}</p>}
                  {copyError && <p role="alert">{copyError}</p>}
                </section>
                <p>
                  <button
                    type="button"
                    onClick={() => {
                      setBookingLayoutAccess({
                        bookingId: createdBooking.bookingId,
                        customerAccessToken: createdBooking.customerAccessToken,
                      });
                      navigate('/booking-layout');
                    }}
                  >
                    Перейти до схеми розсадки
                  </button>
                </p>
              </>
            )}
        </section>
      )}
      {activeMenuAccess && (
        <section aria-label={`Приватне меню бронювання ${activeMenuAccess.bookingId}`}>
          <CustomerEventVenueMenu
            key={`${activeMenuAccess.bookingId}:${activeMenuAccess.customerAccessToken}`}
            bookingId={activeMenuAccess.bookingId}
            customerAccessToken={activeMenuAccess.customerAccessToken}
          />
          <button type="button" onClick={closePrivateMenu}>Завершити перегляд приватного меню</button>
        </section>
      )}
      {!createdBooking && !activeMenuAccess && (
        <form onSubmit={submitBooking} className="legacy-crud-page">
          <div className="legacy-crud-page__form-grid legacy-crud-page__form-grid--two">
            <label>
              Зал
              <select
                required
                disabled={loading}
                value={form.eventVenueId}
                onChange={(event) => {
                  invalidateAvailability();
                  setForm((current) => ({ ...current, eventVenueId: event.target.value, eventDate: '' }));
                  setCreatedBooking(null);
                  setError('');
                }}
              >
                <option value="">Оберіть зал</option>
                {venues.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}{venue.address ? ` — ${venue.address}` : ''}
                  </option>
                ))}
              </select>
              {selectedVenue?.description && <small>{selectedVenue.description}</small>}
            </label>
            <label>
              Дата події
              <input
                type="date"
                required
                min={minimumDate}
                max={maximumDate}
                value={form.eventDate}
                onChange={(event) => updateForm('eventDate', event.target.value)}
              />
              {selectedVenue && (
                <small>
                  Доступний період: {minimumDate} – {maximumDate}.
                </small>
              )}
            </label>
            <label>
              Початок
              <input
                type="time"
                required
                value={form.startTime}
                onChange={(event) => updateForm('startTime', event.target.value)}
              />
            </label>
            <label>
              Завершення
              <input
                type="time"
                required
                value={form.endTime}
                onChange={(event) => updateForm('endTime', event.target.value)}
              />
            </label>
            <label>
              Ім’я та прізвище
              <input
                required
                maxLength={120}
                value={form.customerName}
                onChange={(event) => updateForm('customerName', event.target.value)}
              />
            </label>
            <label>
              Телефон
              <input
                type="tel"
                required
                maxLength={30}
                value={form.phone}
                onChange={(event) => updateForm('phone', event.target.value)}
              />
            </label>
            <label>
              Електронна пошта
              <input
                type="email"
                maxLength={254}
                value={form.email}
                onChange={(event) => updateForm('email', event.target.value)}
              />
            </label>
            <label>
              Кількість гостей
              <input
                type="number"
                required
                min={1}
                max={5000}
                step={1}
                value={form.guestCount}
                onChange={(event) => updateForm('guestCount', event.target.value)}
              />
            </label>
            <label className="legacy-crud-page__form-grid-full">
              Побажання до події
              <textarea
                maxLength={1000}
                rows={3}
                value={form.customerNotes}
                onChange={(event) => updateForm('customerNotes', event.target.value)}
              />
            </label>
          </div>

          <p>
            Після надсилання заявки меню можна скласти лише із затверджених позицій каталогу продажу.{' '}
            <Link to="/menu">Переглянути каталог</Link>.
          </p>

          {availability && (
            <p role="status" style={{ color: availability.isAvailable ? 'var(--crm-success)' : 'var(--crm-danger)' }}>
              {availability.isAvailable
                ? 'Цей зал вільний на обраний час. Остаточне бронювання підтвердить адміністратор після завдатку.'
                : 'Цей зал недоступний на обраний час. Оберіть інший час або дату.'}
            </p>
          )}
          {error && <p role="alert">{error}</p>}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            <button type="button" disabled={loading || checking || submitting} onClick={checkAvailability}>
              {checking ? 'Перевірка...' : 'Перевірити доступність'}
            </button>
            <button
              type="submit"
              disabled={loading || submitting || !availability?.isAvailable || availabilityKey !== currentAvailabilityKey}
            >
              {submitting ? 'Надсилання...' : 'Надіслати заявку'}
            </button>
          </div>
        </form>
      )}
      {!activeMenuAccess && (
        <section aria-labelledby="existing-booking-menu-title" style={{
          margin: '20px 0',
          padding: 16,
          background: 'var(--crm-surface)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
        }}>
          <h3 id="existing-booking-menu-title">Повернутися до меню бронювання</h3>
          <p>
            Введіть номер бронювання та збережений приватний код. Код діє лише у приватному запиті та не зберігається
            у браузері.
          </p>
          <form onSubmit={openExistingMenu} className="legacy-crud-page">
            <label>
              Номер бронювання
              <input
                type="number"
                min={1}
                step={1}
                required
                value={bookingIdInput}
                onChange={(event) => setBookingIdInput(event.target.value)}
              />
            </label>
            <label>
              Приватний код доступу
              <input
                type="password"
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                maxLength={64}
                spellCheck={false}
                required
                value={accessCodeInput}
                onChange={(event) => {
                  setAccessCodeInput(event.target.value.toUpperCase());
                  setAccessError('');
                }}
              />
            </label>
            {accessError && <p role="alert">{accessError}</p>}
            <button type="submit">Відкрити приватне меню</button>
          </form>
        </section>
      )}
      {error && createdBooking && <p role="alert">{error}</p>}
    </main>
  );
};

export default EventVenueBookingPage;
